<template>
  <div>
    <v-row>
      <v-col
        cols="12"
        md="12"
      >
        <div class="d-flex align-center">
          <p class="font-weight-medium mr-3 mb-3">
            Distribution Package info:
          </p>
          <v-divider />
        </div>
        <base-card>
          <v-card-text class="pa-4">
            <div class="d-flex justify-space-between">
              <div class="pr-4">
                <h5>
                  {{ distribPackage.name }}
                </h5>
              </div>
              <div class="pa-2">
                <v-btn
                  :disabled="loading"
                  color="primary"
                  @click="goToEdit(distribPackage)"
                >
                  <i class="mdi mdi-pencil p-r-5" />
                  edit
                </v-btn>
              </div>
            </div>
            <div>
              <span class="font-weight-thin pr-2">MT URL prefix:</span>
              <a
                :href="distribPackage.mtPrefix"
                target="_blank"
                class="ma-0 font-weight-medium"
              >
                {{ distribPackage.mtPrefix }}
              </a>
            </div>
            <div>
              <span class="font-weight-thin pr-2">MT pre-roll URL prefix:</span>
              <a
                :href="distribPackage.mtPrerollPrefix"
                target="_blank"
                class="ma-0 font-weight-medium"
              >
                {{ distribPackage.mtPrerollPrefix }}
              </a>
            </div>
            <div>
              <span class="font-weight-thin pr-2">SSAI Params:</span>
              <code style="font-family: Arial">{{ distribPackage.propsStr || 'N/A' }}</code>
            </div>
          </v-card-text>
        </base-card>
      </v-col>
      <v-col
        cols="12"
        md="12"
      >
        <base-card>
          <div class="shadow-none">
            <div
              class="d-flex justify-space-between flex-wrap align-center"
            >
              <v-card-title>
                <div>
                  <h5 class="text-18">
                    Selected Channels ({{ packageChannels.length }})
                    <v-btn
                      class="ml-2"
                      :disabled="loading"
                      color="primary"
                      @click="selectChannels()"
                    >
                      <i class="mdi mdi-playlist-edit p-r-5" />
                      Add/Delete
                    </v-btn>
                  </h5>
                </div>
              </v-card-title>
              <div class="pa-2">
                <v-btn
                  outlined
                  class="ma-2"
                  color="primary"
                  @click="goToEditMeta(distribPackage)"
                >
                  <i class="mdi mdi-pencil p-r-5" />
                  Edit Metadata
                </v-btn>
                <v-btn
                  outlined
                  class="ma-2"
                  @click="exportXLS()"
                >
                  <i class="mdi mdi-download p-r-5" />
                  Export
                </v-btn>
              </div>
            </div>
          </div>
          <v-card-text>
            <v-data-table
              :headers="headers"
              :items="packageChannels"
              item-key="id"
              class="elevation-1 table-one pointer"
              multi-sort
              :items-per-page="25"
              :footer-props="{
                'items-per-page-options': [25, 50, 100, 150],
                showFirstLastPage: true,
                firstIcon: 'mdi-arrow-collapse-left',
                lastIcon: 'mdi-arrow-collapse-right',
              }"
            >
              <template v-slot:item.id="{item}">
                <div class="d-flex align-center">
                  <p class="ma-0 font-weight-medium">
                    {{ item.id }}
                  </p>
                </div>
              </template>
              <template v-slot:item.name="{item}">
                <div class="d-flex align-center">
                  <v-avatar
                    class="mr-2"
                    size="26"
                    dark
                    tile
                  >
                    <img
                      :src="item.pictureURL"
                      alt=""
                    >
                  </v-avatar>
                  <p class="ma-0 font-weight-medium">
                    {{ item.name }}
                  </p>
                </div>
              </template>
              <template v-slot:item.instance="{item}">
                <div class="font-weight-medium text--disabled text-no-wrap">
                  {{ item.instance }}
                </div>
              </template>
              <template v-slot:item.external="{item}">
                <span
                  v-if="item.external"
                  class="text-no-wrap"
                >
                  <v-icon class="success--text text-body-2">
                    mdi-check
                  </v-icon>
                </span>
              </template>
              <template v-slot:item.url="{item}">
                <v-btn
                  v-if="item.url && item.selected"
                  text
                  color="primary"
                  @click.stop="openPlayer(item)"
                >
                  <v-icon>
                    mdi-play
                  </v-icon>
                  Play
                </v-btn>
                <template v-else-if="!item.selected">
                  <v-chip
                    class=""
                    color="red"
                    label
                    small
                    text-color="white"
                  >
                    <v-icon
                      small
                      left
                    >
                      mdi-minus
                    </v-icon>
                    removing
                  </v-chip>
                </template>
                <template v-else>
                  <v-chip
                    class=""
                    color="orange"
                    label
                    small
                    text-color="white"
                  >
                    <v-icon
                      small
                      left
                      class="fa-spin"
                    >
                      fa fa-circle-notch
                    </v-icon>
                    processing
                  </v-chip>
                </template>
              </template>
              <template v-slot:item.createTime="{item}">
                {{ moment.utc(item.createTime).format('D MMM YYYY') }}
              </template>
              <template v-slot:item.vastId="{ item }">
                <v-text-field
                  v-model="item.vastId"
                  hide-details
                  dense
                  outlined
                  style="max-width: 100px;"
                  @focusout="saveVASTId(item)"
                />
              </template>
              <template v-slot:item.preroll="{ item }">
                <v-checkbox
                  v-model="item.preroll"
                  hide-details
                  dense
                  color="success"
                  @click="savePreroll(item)"
                />
              </template>
            </v-data-table>
          </v-card-text>
        </base-card>
      </v-col>
    </v-row>
    <!-- Player -->
    <v-bottom-sheet
      v-model="playerDialog"
      max-width="960"
      :persistent="false"
      inset
    >
      <v-card tile>
        <iframe
          id="serviceFrameSend"
          :src="frameSrc"
          width="100%"
          height="380"
          frameborder="0"
        />
        <v-card-text class="pa-4">
          <div class="d-flex justify-space-between">
            <div class="pr-4">
              <h5>
                {{ selectedChannel.name }}
              </h5>
            </div>
          </div>
          <div v-if="selectedChannel.sourceURL">
            <span class="font-weight-thin pr-2">Source URL:</span>
            <a
              :href="selectedChannel.sourceURL"
              target="_blank"
              class="ma-0 font-weight-medium"
              :class="{ 'yellow': selectedChannel.sourceURL === frameLink }"
            >
              {{ selectedChannel.sourceURL }}
            </a>
            <v-btn
              v-clipboard="selectedChannel.sourceURL"
              class="m-l-10"
              icon
              @click.stop="handleCopy(selectedChannel.sourceURL)"
            >
              <v-icon>
                mdi-content-copy
              </v-icon>
            </v-btn>
            <v-btn
              text
              @click="setFrameSrc(selectedChannel.sourceURL)"
            >
              <v-icon>
                mdi-play
              </v-icon>
              Play
            </v-btn>
          </div>
          <div>
            <span class="font-weight-thin pr-2">Distrib URL:</span>
            <a
              :href="selectedChannel.url"
              target="_blank"
              class="ma-0 font-weight-medium"
              :class="{ 'yellow': selectedChannel.url === frameLink }"
            >
              {{ selectedChannel.url }}
            </a>
            <v-btn
              v-clipboard="selectedChannel.url"
              class="m-l-10"
              icon
              @click.stop="handleCopy(selectedChannel.url)"
            >
              <v-icon>
                mdi-content-copy
              </v-icon>
            </v-btn>
            <v-btn
              text
              @click="setFrameSrc(selectedChannel.url)"
            >
              <v-icon>
                mdi-play
              </v-icon>
              Play
            </v-btn>
          </div>
          <div v-if="selectedChannel.mtUrl">
            <span class="font-weight-thin pr-2">MT URL:</span>
            <a
              :href="selectedChannel.mtUrl"
              target="_blank"
              class="ma-0 font-weight-medium"
              :class="{ 'yellow': selectedChannel.mtUrl === frameLink }"
            >
              {{ selectedChannel.mtUrl }}
            </a>
            <v-btn
              v-clipboard="selectedChannel.mtUrl"
              class="m-l-10"
              icon
              @click.stop="handleCopy(selectedChannel.mtUrl)"
            >
              <v-icon>
                mdi-content-copy
              </v-icon>
            </v-btn>
            <v-btn
              text
              @click="setFrameSrc(selectedChannel.mtUrl)"
            >
              <v-icon>
                mdi-play
              </v-icon>
              Play
            </v-btn>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            depressed
            @click="closePlayer()"
          >
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-bottom-sheet>
    <!-- Select Channels Form  -->
    <v-dialog
      v-model="selectChannelDialog"
      max-width="800px"
    >
      <v-card>
        <v-card-title>
          <span class="text-h5">Selected channels ({{ selectedChannels.length }})</span>
        </v-card-title>
        <v-card-text>
          <v-container>
            <v-data-table
              v-model="selectedChannels"
              :headers="channelHeaders"
              show-select
              :items="channels"
              item-key="id"
              class="elevation-1 table-one pointer"
              multi-sort
              dense
              :items-per-page="15"
              :footer-props="{
                'items-per-page-options': [15, 30, 50],
                showFirstLastPage: true,
                firstIcon: 'mdi-arrow-collapse-left',
                lastIcon: 'mdi-arrow-collapse-right',
              }"
            >
              <template v-slot:item.name="{item}">
                <p class="ma-0 font-weight-medium">
                  {{ item.name }}
                </p>
              </template>
              <template v-slot:item.external="{item}">
                <span
                  v-if="item.external"
                  class="text-no-wrap"
                >
                  <v-icon class="success--text text-body-2">
                    mdi-check
                  </v-icon>
                </span>
              </template>
              <template v-slot:item.instance="{item}">
                {{ item.instance }}
              </template>
            </v-data-table>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            color="blue darken-1"
            text
            @click="selectChannelDialog = false"
          >
            Close
          </v-btn>
          <v-btn
            color="primary"
            :disabled="loading"
            @click="setChannels()"
          >
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- Patch Name Form  -->
    <v-dialog
      v-model="patchDialog"
      max-width="800px"
    >
      <v-card>
        <v-card-title>
          <span class="text-h5">Update Distribution Package</span>
        </v-card-title>
        <v-card-text>
          <v-container>
            <v-row>
              <v-col
                cols="12"
                sm="12"
              >
                <v-text-field
                  v-model="patched.name"
                  label="Name*"
                  :counter="255"
                  :rules="rules.nameRules"
                  required
                />
              </v-col>
              <v-col
                cols="12"
                sm="12"
              >
                <v-text-field
                  v-model="patched.mtPrefix"
                  label="MT URL prefix"
                />
              </v-col>
              <v-col
                cols="12"
                sm="12"
              >
                <v-text-field
                  v-model="patched.mtPrerollPrefix"
                  label="MT pre-roll URL prefix"
                />
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            color="blue darken-1"
            text
            @click="patchDialog = false"
          >
            Close
          </v-btn>
          <v-btn
            color="primary"
            :disabled="loading || !isPatchFormValid"
            @click="updatePack()"
          >
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
  import { processDistributionPackage, processDistribution, processThirdPartyChannel } from '@/store/converters'

  export default {
    metaInfo: {
      title: 'Distribution Package Details',
    },
    data () {
      return {
        distribPackage: {},
        packageChannels: [],
        headers: [
          { text: 'Channel Name', align: 'start', value: 'channelName' },
          { text: 'Company', value: 'instance' },
          { text: '3rd party', value: 'external' },
          { text: 'Distribution URL', sortable: false, value: 'url' },
          { text: 'Added', value: 'createTime' },
          {
            text: 'VAST ID',
            value: 'vastId',
          },
          {
            text: 'Pre-roll',
            value: 'preroll',
          },
        ],
        channels: [],
        channelMap: {},
        thirdPartyChannelMap: {},
        selectedChannel: {
          name: '',
          url: '',
          mtUrl: '',
          sourceURL: '',
        },
        frameSrc: '',
        frameLink: '',
        playerDialog: false,
        patchDialog: false,
        patched: {
          name: '',
          mtPrefix: '',
          mtPrerollPrefix: '',
        },
        rules: {
          nameRules: [
            v => !!v || 'Name is required',
            v => (v.length >= 3 && v.length <= 255) || 'Name must be between 3 and 255 characters',
          ],
        },
        selectChannelDialog: false,
        selectedChannels: [],
        channelHeaders: [
          {
            text: 'Name',
            align: 'start',
            sortable: true,
            value: 'name',
          },
          { text: '3rd party', sortable: true, value: 'external' },
          { text: 'Company', value: 'instance' },
        ],
        polling: null,
      }
    },
    computed: {
      loading () {
        return this.$store.getters.isDataLoaded
      },
      isPatchFormValid () {
        if (!(this.patched.name.length >= 3 && this.patched.name.length <= 255)) {
          return false
        }
        return true
      },
    },
    watch: {
      'playerDialog' (to, from) {
        if (to === false) {
          this.closePlayer()
        }
      },
    },
    created () {
      this.getPackageInfo(this.$route.params.distribution_id)
      this.pollData()
    },
    beforeDestroy () {
      clearInterval(this.polling)
    },
    methods: {
      pollData () {
        this.polling = setInterval(() => {
          if (!this.loading) {
            this.getChannels()
          }
        }, 10000)
      },
      getPackageInfo (id) {
        this.$store.commit('setPageLoader')

        const args = {
          'id[eq]': id,
        }
        const packReq = this.$store.dispatch('distributionPackages', args)

        const channelArgs = {
          'suspended[eq]': 0,
          $expand: 'instance',
        }
        const channelReq = this.$store.dispatch('channels', channelArgs)

        const packChannelReq = this.$store.dispatch('distributionPackageChannels', id)

        const thirdPartyChannelArgs = {}
        const thirdPartyChannelReq = this.$store.dispatch('thirdPartyChannels', thirdPartyChannelArgs)

        return Promise.all([packReq, channelReq, thirdPartyChannelReq, packChannelReq])
          .then((res) => {
            const packages = res[0] || []
            if (!packages.length) {
              this.$router.push({ name: 'Distributions' })
              return
            }
            const pack = processDistributionPackage(packages[0])
            const fields = []
            for (let i = 0; i < pack.props.length; i += 1) {
              const prop = pack.props[i]
              if (prop.include) {
                const value = prop.auto ? '[[auto]]' : encodeURIComponent(prop.value)
                fields.push(prop.key + '=' + value)
              }
            }
            pack.propsStr = fields.join('&')
            this.distribPackage = pack

            const channels = res[1] || []
            const processed = []
            for (let i = 0; i < channels.length; i += 1) {
              const ch = channels[i]
              const channel = {
                id: ch.id,
                name: ch.name,
                instance: ch.instance.name,
                external: false,
              }
              processed.push(channel)
              this.channelMap[ch.id] = channel
            }

            const thirdPartyChannels = res[2] || []
            const thirdPartyChannelsProcessed = thirdPartyChannels.map((ch) => processThirdPartyChannel(ch))
            for (let i = 0; i < thirdPartyChannelsProcessed.length; i += 1) {
              const ch = thirdPartyChannelsProcessed[i]
              const channel = {
                id: '3rd' + ch.id,
                name: ch.name,
                instance: ch.company,
                external: true,
                sourceURL: ch.url,
              }
              processed.push(channel)
              this.thirdPartyChannelMap[ch.id] = channel
            }

            this.channels = processed

            const packChannels = res[3] || []
            this.processPackageChannels(packChannels)
          })
          .catch((errorMsg) => {
            this.$store.commit('showSnackbar', {
              text: errorMsg,
              timeout: 6000,
              color: 'error',
            })
            this.$router.push({ name: 'Distributions' })
          })
          .finally(() => this.$store.commit('resolvePageLoader'))
      },
      processPackageChannels (packChannels) {
        const processed = []

        for (let i = 0; i < packChannels.length; i += 1) {
          const d = packChannels[i]
          // eslint-disable-next-line no-prototype-builtins
          if (this.channelMap.hasOwnProperty(d.channel_id)) {
            const ch = this.channelMap[d.channel_id]
            let streamURL = ''
            if (d.url) {
              streamURL = d.url.replace('$origin', 'now')
              streamURL = streamURL.replace('$offset', '-1m_15s')
              streamURL = streamURL.replace(',', '_')
            }
            processed.push({
              id: d.id,
              channelId: ch.id,
              channelName: ch.name,
              instance: ch.instance,
              url: streamURL,
              createTime: d.create_time,
              vastId: d.vast_id || '',
              preroll: d.preroll,
              selected: d.selected,
              external: false,
            })
          }
          // eslint-disable-next-line no-prototype-builtins
          if (this.thirdPartyChannelMap.hasOwnProperty(d.third_party_channel_id)) {
            const ch = this.thirdPartyChannelMap[d.third_party_channel_id]
            processed.push({
              id: d.id,
              channelId: ch.id,
              channelName: ch.name,
              instance: ch.instance,
              url: d.url,
              createTime: d.create_time,
              vastId: d.vast_id || '',
              preroll: d.preroll,
              selected: d.selected,
              external: true,
              sourceURL: ch.sourceURL,
            })
          }
        }
        this.packageChannels = processed
      },
      getChannels () {
        this.$store.dispatch('distributionPackageChannels', this.distribPackage.id)
          .then((response) => {
            const packChannels = response || []
            this.processPackageChannels(packChannels)
          })
          .catch((errorMsg) => {
            console.log('error getting distrib channels', errorMsg)
          })
      },
      getDistribution (id) {
        this.$store.commit('setPageLoader')

        const args = {
          'id[eq]': id,
        }
        const distribReq = this.$store.dispatch('distributions', args)

        return Promise.all([distribReq])
          .then((res) => {
            const distributions = res[0]
            this.distribution = processDistribution(distributions[0])
            for (const key in this.distribution.urls) {
              if (['hls3', 'hls5', 'hls7', 'dash'].includes(key)) {
                this.urls.push({
                  type: key,
                  url: this.distribution.urls[key],
                })
              }
            }
            this.whitelist = this.distribution.whitelist
          })
          .catch((errorMsg) => {
            this.$store.commit('showSnackbar', {
              text: errorMsg,
              timeout: 6000,
              color: 'error',
            })
            this.$router.push({ name: 'Distributions' })
          })
          .finally(() => {
            this.$store.commit('resolvePageLoader')
          })
      },
      handleCopy () {
        this.$store.commit('showSnackbar', {
          text: 'URL has been copied',
          timeout: 6000,
          color: 'success',
        })
      },
      openPlayer (item) {
        const url = item.url
        this.selectedChannel.name = item.channelName
        this.selectedChannel.url = url.replace('distrib010.noisypeak.com', 'distrib.noisypeak.com')
        this.setFrameSrc(this.selectedChannel.url)
        this.selectedChannel.sourceURL = item.sourceURL
        if (this.distribPackage.mtPrefix) {
          this.selectedChannel.mtUrl = url.replace('https://distrib.noisypeak.com/d/', this.distribPackage.mtPrefix) + '?ads.vast_id=' + item.vastId
        }
        this.playerDialog = true
      },
      setFrameSrc (url) {
        this.frameLink = url
        const link = url.replace(/^http:\/\//, 'https://')
        this.frameSrc = `/js/radiant/index.html?src=${link}`
      },
      closePlayer () {
        this.playerDialog = false
        this.setFrameSrc('')
      },
      openRemoveDialog (i) {
        this.deletedSubnet = i
        this.removeDialog = true
      },
      editPackage () {
        this.patched.name = this.distribPackage.name
        this.patched.mtPrefix = this.distribPackage.mtPrefix
        this.patched.mtPrerollPrefix = this.distribPackage.mtPrerollPrefix
        this.patchDialog = true
      },
      updatePack () {
        this.$store.commit('setPageLoader')

        const pack = {
          id: this.distribPackage.id,
          name: this.patched.name,
          mt_prefix: this.patched.mtPrefix,
          mt_preroll_prefix: this.patched.mtPrerollPrefix,
        }

        this.$store.dispatch('patchDistributionPackage', pack)
          .then(() => {
            this.$store.commit('showSnackbar', {
              text: 'The package has been successfully updated',
              timeout: 6000,
              color: 'success',
            })
            this.distribPackage.name = this.patched.name
            this.distribPackage.mtPrefix = this.patched.mtPrefix
            this.distribPackage.mtPrerollPrefix = this.patched.mtPrerollPrefix
          })
          .catch((errorMsg) => {
            this.$store.commit('showSnackbar', {
              text: errorMsg,
              timeout: 6000,
              color: 'error',
            })
          })
          .finally(() => {
            this.patchDialog = false
            this.$store.commit('resolvePageLoader')
          })
      },
      selectChannels () {
        this.selectedChannels = []
        for (let i = 0; i < this.packageChannels.length; i += 1) {
          const d = this.packageChannels[i]
          this.selectedChannels.push({
            id: d.channelId,
            name: d.channelName,
            instance: d.instance,
          })
        }
        this.selectChannelDialog = true
      },
      setChannels () {
        this.$store.commit('setPageLoader')

        const ids = []
        const thirdPartyChannelIds = []
        for (let i = 0; i < this.selectedChannels.length; i += 1) {
          const id = this.selectedChannels[i].id
          if (id.toString().startsWith('3rd')) {
            thirdPartyChannelIds.push(Number(id.replace('3rd', '')))
          } else {
            ids.push(this.selectedChannels[i].id)
          }
        }

        const data = {
          id: this.distribPackage.id,
          channel_id: ids,
          third_party_channel_id: thirdPartyChannelIds,
        }

        this.$store.dispatch('setDistributionPackageChannels', data)
          .then(() => {
            this.getChannels()
            this.selectChannelDialog = false
          })
          .catch((errorMsg) => {
            this.$store.commit('showSnackbar', {
              text: errorMsg,
              timeout: 6000,
              color: 'error',
            })
          })
          .finally(() => {
            this.$store.commit('resolvePageLoader')
          })
      },
      saveVASTId (item) {
        item.vastId = item.vastId.trim()

        const params = {
          id: this.distribPackage.id,
          vast_id: item.vastId,
          distrib_id: item.id,
        }

        this.$store.dispatch('setDistributionVastId', params)
          .then(() => {
            this.$store.commit('showSnackbar', {
              text: 'The vast ID has been successfully updated',
              timeout: 6000,
              color: 'success',
            })
          })
          .catch((errorMsg) => {
            this.$store.commit('showSnackbar', {
              text: errorMsg,
              timeout: 6000,
              color: 'error',
            })
          })
      },
      savePreroll (item) {
        const params = {
          id: this.distribPackage.id,
          preroll: item.preroll ? 1 : 0,
          distrib_id: item.id,
        }

        this.$store.dispatch('setDistributionPreroll', params)
          .then(() => {
            this.$store.commit('showSnackbar', {
              text: 'The distribution has been successfully updated',
              timeout: 6000,
              color: 'success',
            })
          })
          .catch((errorMsg) => {
            this.$store.commit('showSnackbar', {
              text: errorMsg,
              timeout: 6000,
              color: 'error',
            })
          })
      },
      exportXLS () {
        this.$store.dispatch('exportDistributionPackage', this.distribPackage.id)
          .then((response) => {
            const blob = new Blob([response.data], { type: response.data.type })
            const link = document.createElement('a')
            link.href = URL.createObjectURL(blob)
            link.download = this.distribPackage.name
            link.click()
            URL.revokeObjectURL(link.href)
          })
          .catch((errorMsg) => {
            this.$store.commit('showSnackbar', {
              text: errorMsg,
              timeout: 6000,
              color: 'error',
            })
          })
      },
      goToEdit (distribution) {
        this.$router.push({
          name: 'EditDistribution',
          params: {
            distribution_id: distribution.id,
          },
        })
      },
      goToEditMeta (distribution) {
        this.$router.push({
          name: 'EditDistributionMeta',
          params: {
            distribution_id: distribution.id,
          },
        })
      },
    },
  }
</script>

<style>
@media (min-width: 1200px) {
  .container {
    max-width: 1320px !important;
  }
}
</style>

